import { useState } from "react";
import { toast } from "sonner";
import { Gauge, Pencil } from "lucide-react";
import { formatApiErrorDetail } from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

export const LOAD_SETS = [
  { key: "gever", label: "Opdrachtgever", short: "Gever" },
  { key: "nemer", label: "Opdrachtnemer", short: "Nemer" },
  { key: "werkelijk", label: "Werkelijk", short: "Werkelijk" },
];

const total = (l) => (l ? l.uren * l.medewerkers * l.weken : null);
const fmt = (n) => (n === null ? "–" : Number(n.toFixed(1)).toLocaleString("nl-NL"));

function LoadRow({ set, load, canEdit, onSave, gid }) {
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState({});
  const start = () => {
    setF({ uren: load?.uren ?? "", medewerkers: load?.medewerkers ?? "", weken: load?.weken ?? "" });
    setEdit(true);
  };
  const save = async () => {
    const v = { uren: Number(f.uren), medewerkers: Number(f.medewerkers), weken: Number(f.weken) };
    if (v.uren < 0 || v.uren > 168 || v.medewerkers < 0 || v.medewerkers > 10000 || v.weken < 0 || v.weken > 520 || !Number.isInteger(v.medewerkers) || !Number.isInteger(v.weken)) {
      toast.error("Vul 0–168 uur per week, een heel aantal medewerkers en 0–520 weken in");
      return;
    }
    try {
      await onSave(set.key, v);
      setEdit(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Opslaan mislukt");
    }
  };
  const id = `goal-load-${gid}-${set.key}`;
  if (edit) {
    return (
      <div className="flex flex-wrap items-center gap-1.5 text-xs py-1" data-testid={`${id}-form`}>
        <span className="w-20 font-mono text-[10px] uppercase tracking-widest text-zinc-500">{set.short}</span>
        <Input type="number" min={0} max={168} value={f.uren} onChange={(e) => setF({ ...f, uren: e.target.value })} className="h-7 w-16 rounded-none text-xs" data-testid={`${id}-uren`} /> u/wk ×
        <Input type="number" min={0} max={10000} value={f.medewerkers} onChange={(e) => setF({ ...f, medewerkers: e.target.value })} className="h-7 w-14 rounded-none text-xs" data-testid={`${id}-medewerkers`} /> mw ×
        <Input type="number" min={0} max={520} value={f.weken} onChange={(e) => setF({ ...f, weken: e.target.value })} className="h-7 w-14 rounded-none text-xs" data-testid={`${id}-weken`} /> wk
        <Button size="sm" className="h-7 rounded-none bg-zinc-900 px-2 text-xs" disabled={[f.uren, f.medewerkers, f.weken].some((v) => v === "")} onClick={save} data-testid={`${id}-save`}>Opslaan</Button>
        <button onClick={() => setEdit(false)} className="text-zinc-400 hover:text-zinc-900 px-1">×</button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 text-xs py-0.5" data-testid={id}>
      <span className="w-20 font-mono text-[10px] uppercase tracking-widest text-zinc-500">{set.short}</span>
      {load ? (
        <span className="flex-1"><span className="font-semibold">{load.uren}</span> u/wk × <span className="font-semibold">{load.medewerkers}</span> mw × <span className="font-semibold">{load.weken}</span> wk = <span className="font-semibold">{fmt(total(load))} uur</span></span>
      ) : <span className="flex-1 text-zinc-400">nog niet ingevuld</span>}
      {canEdit && <button onClick={start} className="text-zinc-400 hover:text-zinc-900" title={`Belasting ${set.label.toLowerCase()} invullen`} data-testid={`${id}-edit`}><Pencil className="h-3 w-3" /></button>}
    </div>
  );
}

export const GoalLoad = ({ goal, canGiver, canTaker, onSave }) => {
  const load = goal.load || {};
  const g = total(load.gever);
  const w = total(load.werkelijk);
  const diff = g && w !== null ? Math.round(((w - g) / g) * 100) : null;
  return (
    <div className="mt-2 border border-zinc-200 bg-white px-2 py-1.5" data-testid={`goal-load-${goal.id}`}>
      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
        <Gauge className="h-3 w-3" /> Belasting
        {diff !== null && <span className={`ml-auto normal-case tracking-normal ${diff > 10 ? "text-red-600" : "text-zinc-500"}`} data-testid={`goal-load-diff-${goal.id}`}>werkelijk {diff > 0 ? "+" : ""}{diff}% t.o.v. gever</span>}
      </div>
      {LOAD_SETS.map((s) => (
        <LoadRow key={s.key} set={s} gid={goal.id} load={load[s.key]} canEdit={s.key === "gever" ? canGiver : canTaker} onSave={(set, v) => onSave(goal.id, set, v)} />
      ))}
    </div>
  );
};
